import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  Wallet, 
  Award,
  ArrowLeft,
  RefreshCw,
  Building2,
  Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatCurrencyFull = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#161C22] border border-white/10 rounded-xl p-4 shadow-xl">
        <p className="text-white font-semibold mb-2">{payload[0]?.payload?.month_full}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrencyFull(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function FundGrowth() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/fund/stats`);
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError(t('fundGrowth.errorLoading'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E14] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0E14] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={fetchStats} className="bg-emerald-500 hover:bg-emerald-600">
            {t('fundGrowth.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  // Show message when no manual data exists
  if (stats && !stats.has_manual_data) {
    return (
      <div className="min-h-screen bg-[#0A0E14] p-4 sm:p-6 lg:p-8" data-testid="fund-growth-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5 text-slate-400" />
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white font-['Outfit']">{t('fundGrowth.title')}</h1>
                  <p className="text-slate-400 text-sm">{t('fundGrowth.subtitle')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* No Data Message */}
          <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/10">
            <Building2 className="w-16 h-16 text-slate-500 mx-auto mb-6" />
            <h2 className="text-xl font-semibold text-white mb-3">{t('fundGrowth.noDataTitle')}</h2>
            <p className="text-slate-400 max-w-md mx-auto">{t('fundGrowth.noDataMessage')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E14] p-4 sm:p-6 lg:p-8" data-testid="fund-growth-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white font-['Outfit']">{t('fundGrowth.title')}</h1>
                <p className="text-slate-400 text-sm">{t('fundGrowth.subtitle')}</p>
              </div>
            </div>
          </div>
          <Button
            onClick={fetchStats}
            variant="outline"
            size="sm"
            className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
            data-testid="refresh-stats-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('fundGrowth.refresh')}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-cards-grid">
          {/* Total Capital */}
          <div style={{background: "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "1rem", backdropFilter: "blur(10px)"}} className=" p-5 relative overflow-hidden group" data-testid="total-capital-card">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 text-emerald-400 mb-2">
                <Wallet className="w-5 h-5" />
                <span className="text-xs font-medium uppercase tracking-wider">{t('fundGrowth.totalCapital')}</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-white font-['Outfit']">
                {formatCurrency(stats?.total_capital || 0)}
              </p>
              <p className="text-xs text-slate-500 mt-1">{t('fundGrowth.fundsUnderManagement')}</p>
            </div>
          </div>

          {/* Total Returns Paid */}
          <div style={{background: "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "1rem", backdropFilter: "blur(10px)"}} className=" p-5 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 text-blue-400 mb-2">
                <TrendingUp className="w-5 h-5" />
                <span className="text-xs font-medium uppercase tracking-wider">{t('fundGrowth.returnsPaid')}</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-white font-['Outfit']">
                {formatCurrency(stats?.total_returns_paid || 0)}
              </p>
              <p className="text-xs text-slate-500 mt-1">{t('fundGrowth.totalDistributed')}</p>
            </div>
          </div>

          {/* Total Investors */}
          <div style={{background: "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "1rem", backdropFilter: "blur(10px)"}} className=" p-5 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 text-purple-400 mb-2">
                <Users className="w-5 h-5" />
                <span className="text-xs font-medium uppercase tracking-wider">{t('fundGrowth.investors')}</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-white font-['Outfit']">
                {stats?.total_investors || 0}
              </p>
              <p className="text-xs text-slate-500 mt-1">{t('fundGrowth.activeParticipants')}</p>
            </div>
          </div>

          {/* Average Return Rate */}
          <div style={{background: "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "1rem", backdropFilter: "blur(10px)"}} className=" p-5 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-2 text-amber-400 mb-2">
                <Award className="w-5 h-5" />
                <span className="text-xs font-medium uppercase tracking-wider">{t('fundGrowth.averageRate')}</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-white font-['Outfit']">
                {stats?.average_monthly_growth?.toFixed(1) || 0}%
              </p>
              <p className="text-xs text-slate-500 mt-1">{t('fundGrowth.monthlyReturn')}</p>
            </div>
          </div>
        </div>

        {/* Main Chart - Capital Growth */}
        <div style={{background: "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "1rem", backdropFilter: "blur(10px)"}} className=" p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{t('fundGrowth.capitalEvolution')}</h2>
              <p className="text-sm text-slate-400">{t('fundGrowth.capitalEvolutionSubtitle')}</p>
            </div>
          </div>
          
          <div className="h-[300px] sm:h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats?.growth_history || []}>
                <defs>
                  <linearGradient id="capitalGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis 
                  dataKey="month" 
                  stroke="#64748b" 
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={{ stroke: '#1e293b' }}
                />
                <YAxis 
                  stroke="#64748b"
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
                  axisLine={{ stroke: '#1e293b' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="cumulative_capital"
                  name={t('fundGrowth.totalCapital')}
                  stroke="#10b981"
                  strokeWidth={3}
                  fill="url(#capitalGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Secondary Chart - Monthly Profit */}
        <div style={{background: "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "1rem", backdropFilter: "blur(10px)"}} className=" p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{t('fundGrowth.monthlyProfit')}</h2>
              <p className="text-sm text-slate-400">{t('fundGrowth.monthlyProfitSubtitle')}</p>
            </div>
          </div>
          
          <div className="h-[300px] sm:h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.growth_history || []}>
                <defs>
                  <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis 
                  dataKey="month" 
                  stroke="#64748b"
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  axisLine={{ stroke: '#1e293b' }}
                />
                <YAxis 
                  stroke="#64748b"
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
                  axisLine={{ stroke: '#1e293b' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="profit" 
                  name={t('fundGrowth.monthlyProfitLabel')} 
                  fill="url(#profitGradient)" 
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center">
          <p className="text-slate-500 text-sm">
            {t('fundGrowth.realTimeData')}
          </p>
        </div>
      </div>
    </div>
  );
}
